import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import AskAnythingHeader from '@/components/askAnything/ask/AskAnythingHeader'
import AskAnythingContent from '@/components/askAnything/ask/AskAnythingContent'
import NewChatCompose from '@/components/askAnything/ask/NewChatCompose'
import ThreadsMenu from '@/components/chat/ThreadsMenu'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { customFetch } from '@/utils/fetch'
import { toast } from '@/hooks/use-toast'
import { useChatStore } from './chatStore'
import { useTheme } from '@/context/ThemeContext'
import { nanoid } from '@/lib/nanoid'
import { AuthContext } from '@/context/AuthContext'

const SkeletonLoader = () => {
  const { isDarkMode } = useTheme()

  return (
    <div className={`absolute inset-0 flex ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      <div className="w-[320px] h-full border-r">
        <div className="p-4 border-b">
          <Skeleton className="h-8 w-full mb-4" />
          <div className="space-y-3 mt-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-4/5" />
          </div>
        </div>
      </div>
      <div className="flex-1 flex flex-col">
        <div className="h-topbar border-b flex items-center px-6">
          <Skeleton className="h-8 w-40" />
          <div className="ml-auto flex items-center gap-2">
            <Skeleton className="h-8 w-8 rounded-full" />
          </div>
        </div>
        <div className="flex-1 p-6 space-y-6 overflow-hidden">
          <div className="flex items-start gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-20 w-[400px]" />
            </div>
          </div>
          <div className="flex items-start gap-3 justify-end">
            <div className="space-y-2">
              <Skeleton className="h-4 w-40 ml-auto" />
              <Skeleton className="h-12 w-[300px]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function AskAnything() {
  const { threadId: threadIdParam } = useParams()
  const navigate = useNavigate()

  function handleClose() {
    const previousPath = localStorage.getItem('previousPath')
    if (previousPath) {
      navigate(previousPath)
    } else {
      navigate('/')
    }
  }

  function handleNewChatClick() {
    navigate('/ask-anything')
  }

  function switchThread(threadId: string) {
    navigate(`/ask-anything/${threadId}`)
  }

  return (
    <AskAnythingInternal
      threadIdParam={threadIdParam}
      onClose={handleClose}
      onNewChatClick={handleNewChatClick}
      switchThread={switchThread}
    />
  )
}

export function AskAnythingInternal({
  threadIdParam,
  onClose,
  onNewChatClick,
  switchThread,
}: {
  threadIdParam?: string
  onClose: () => void
  onNewChatClick: () => void
  switchThread: (threadId: string) => void
}) {
  const {
    threads,
    isLoading,
    selectedProduct,
    initData,
    error,
    threadId,
    setThreadId,
    setIsEmptyThread,
    isEmptyThread,
  } = useChatStore()
  const { user, isLoading: isAuthLoading, isAuthenticated } = React.useContext(AuthContext)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [localError, setLocalError] = useState<string | null>(null)
  const { isDarkMode } = useTheme()

  useLayoutEffect(() => {
    console.log('-- threadIdParam', threadIdParam)
    if (threadIdParam) {
      setThreadId(threadIdParam)
      setIsEmptyThread(false)
    } else {
      setIsEmptyThread(true)
      setThreadId(nanoid())
    }
  }, [threadIdParam])

  useEffect(() => {
    // Wait until auth state is resolved before initializing data to avoid premature 401s
    if (isAuthLoading || !isAuthenticated) return

    const init = async () => {
      if (!threadId) {
        return
      }

      try {
        await initData(threadId)
      } catch (error) {
        console.error('Error initializing data:', error)
        setLocalError('Failed to load chat data. Please try again later.')
      } finally {
        setIsInitialLoading(false)
      }
    }
    init()
  }, [threadId, isAuthLoading, isAuthenticated])

  // Handle loading state (also show while auth is loading)
  if (isInitialLoading || isAuthLoading) {
    return <SkeletonLoader />
  }

  // Handle error state
  if (error || localError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-5 w-5" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error || localError}
            <div className="mt-4">
              <Button variant="outline" onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  // Show new chat compose mode when no threadId is present
  if (isEmptyThread || !selectedProduct) {
    return (
      <div className={`absolute inset-0 flex ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white'}`}>
        <ThreadsMenu currentThreadId="" onNewChatClick={onNewChatClick} switchThread={switchThread} />
        <div className="flex-1">
          <AskAnythingHeader isComposeMode={true} onClose={onClose} />
          <NewChatCompose onClose={onClose} />
        </div>
      </div>
    )
  }

  return (
    <div className={`absolute inset-0 flex ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white'}`}>
      <ThreadsMenu currentThreadId={threadId} onNewChatClick={onNewChatClick} switchThread={switchThread} />

      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Content area takes full height and can scroll behind the header */}
        <div className="absolute inset-0 z-0">
          <AskAnythingContent currentThreadId={threadId} />
        </div>

        {/* Header floats on top with a higher z-index */}
        <div className="sticky top-0 left-0 right-0 z-10">
          <AskAnythingHeader isComposeMode={false} onClose={onClose} />
        </div>
      </div>
    </div>
  )
}
